import { createColumnHelper } from '@tanstack/react-table';
import classNames from 'classnames';
import { format } from 'date-fns-tz';
import { useTranslation } from 'next-i18next';
import Tooltip from 'react-tooltip-lite';
import { formatNumericValue, truncateString } from 'utils/general';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import { IPeriodicTasks } from '@/modules/periodicTasks/types';
import { useGetPropertyConfigurations } from '@/modules/settings/property/apiHooks';
import { IWarehouseManagement } from '@/modules/warehouseManagement/shared/types';
import { Typography } from '@/shared/components/atoms';
import { IIconWithTitleDescriptionProps } from '@/shared/components/atoms/IconWithTItleDescription/IconWithTitleDescription';
import { TypographyProps } from '@/shared/components/atoms/Typography/Typography.types';
import { IDetailsPageHeaderProps } from '@/shared/components/molecules/DetailsPageHeader/DetailsPageHeader';
import PartialListWithTooltip from '@/shared/components/molecules/PartialListWithTooltip';
import { useStore } from '@/shared/hooks/context/createGenericContext';

import Arrow from '~/images/shared/ArrowUp.svg';
import Clipboard from '~/images/shared/Clipboard.svg';
import InfoCircle from '~/images/shared/ExleminationMark.svg';
import Farmer from '~/images/shared/Farmer.svg';
import Location from '~/images/shared/Location.svg';
import PictureFrame from '~/images/shared/picture-frame.svg';
import Speedometer from '~/images/shared/Speedometer.svg';
import Sprout from '~/images/shared/sprout.svg';
import HexagonalWarning from '~/images/shared/warning-2.svg';

export enum PeriodicOperationCategory {
  Fertigation = 1,
  Spraying = 2,
  Irrigation = 3,
  Planting = 4,
}

export const usePeriodicTasksDetailsUtils = () => {
  const { t } = useTranslation();

  const [selectedAsset] = useStore((store) => store?.asset);

  const { data: configurations } = useGetPropertyConfigurations(
    selectedAsset?.id
  );
  const areaUnit = configurations?.areaUnit ?? 'acre';

  const titleTypography: Omit<TypographyProps, 'children'> = {
    variant: 'subtitle2',
    weight: 'semiBold',
  };
  const descriptionTypography: Omit<TypographyProps, 'children'> = {
    variant: 'h3',
    weight: 'semiBold',
    className: 'text-primary-400',
  };

  const getHeaderCardProps = (
    data: IPeriodicTasks.ITaskDetailsHeaderCardProps
  ): IDetailsPageHeaderProps => {
    const timeline = [
      {
        title: t('created-on'),
        date: data.timeline.creationDate,
      },
      {
        title: t('planned-for'),
        date: data.timeline.plannedDate,
      },
    ];

    if (data.timeline.startDate && data.timeline.applicationDate)
      timeline.push(
        { title: t('started-on'), date: data.timeline.startDate },
        { title: t('applied-on'), date: data.timeline.applicationDate }
      );

    return {
      title: `${data.taskInfo.name} #${data.id}`,
      icon: data.taskInfo.categoryIcon,
      status: {
        color: data.status === 'apply' ? 'green' : 'yellow',
        label: data.status === 'apply' ? t('ended') : t('todo'),
      },
      timeline,
    };
  };

  const getMainInfoCardItems = (
    data: IPeriodicTasks.ITaskDetailsMainInfoProps
  ): IIconWithTitleDescriptionProps[] => {
    const items: IIconWithTitleDescriptionProps[] = [
      {
        icon: <Clipboard width='32' height='32' />,
        iconGap: 'gap-16',
        title: t('descriptive-name'),
        titleTypography,
        descriptionTypography,
        description: (
          <div title={data.descriptiveName}>
            {truncateString({ text: data.descriptiveName, length: 30 })}
          </div>
        ),
        iconAlignment: 'center',
      },
      {
        icon: <Sprout width='32' height='32' />,
        iconGap: 'gap-16',
        title: t('crop'),
        description: (
          <PartialListWithTooltip
            limit={2}
            items={data.crops.map((crop) => ({
              description: crop.crop,
              imageURL: crop?.image,
            }))}
          />
        ),
        titleTypography,
        iconAlignment: 'center',
      },
      {
        icon: <Location width='32' height='32' />,
        iconGap: 'gap-16',
        title: t('work-locations'),
        titleTypography,
        iconAlignment: 'center',
        description: (
          <PartialListWithTooltip
            limit={2}
            items={data.plots.map((plot) => ({
              description: plot.name,
              imageURL: plot.cropImage,
            }))}
          />
        ),
        iconContainerClassName: 'text-primary',
      },
      {
        icon: (
          <span className='text-primary-default'>
            <Farmer width='32' height='32' />
          </span>
        ),
        iconGap: 'gap-16',
        title: t('created-by-whom'),
        titleTypography,
        descriptionTypography,
        description: data.createdBy,
        iconAlignment: 'center',
      },
      {
        icon: (
          <span className='text-primary-default'>
            <Farmer width='32' height='32' />
          </span>
        ),
        iconGap: 'gap-16',
        title: t('assigned-to-who'),
        titleTypography,
        descriptionTypography,
        description:
          !!data?.assigneeName && !!data.assigneeName.length
            ? data.assigneeName
            : '-',
        iconAlignment: 'center',
      },
    ];

    if (
      data?.causes &&
      data.taskInfo.categoryId === PeriodicOperationCategory.Spraying
    )
      items.splice(3, 0, {
        icon: <HexagonalWarning width='32' height='32' />,
        iconGap: 'gap-16',
        title: t('cause'),
        titleTypography,
        description: (
          <PartialListWithTooltip
            limit={2}
            items={data.causes.map((cause) => ({
              description: cause.cause,
            }))}
          />
        ),
        iconAlignment: 'center',
      });

    return items;
  };

  const getFieldsTable = (footerSum: number) => {
    const columnHelper =
      createColumnHelper<IPeriodicTasks.ITaskDetailsPlotSchema>();
    return [
      columnHelper.accessor('name', {
        header: t('field-category'),
        cell: ({ getValue }) => (
          <PartialListWithTooltip
            limit={1}
            items={[
              {
                description: getValue(),
              },
            ]}
          />
        ),
        footer: t('total-work-area'),
      }),
      columnHelper.accessor('crop', {
        header: t('crop'),
        cell: ({ getValue, row: { original } }) => (
          <PartialListWithTooltip
            limit={1}
            items={[
              {
                description: getValue(),
                imageURL: original.cropImage,
              },
            ]}
          />
        ),
      }),
      // TODO: add time zone here
      // format(new Date(value), 'h', {timezone: ex})
      columnHelper.accessor('startTime', {
        header: t('applying-time'),
        cell: ({ getValue, row: { original } }) => {
          const value = getValue();
          return value && original.endTime
            ? `${t('from')} ${format(new Date(value), 'h:mm')} ${t(
                format(new Date(value), 'aaa')
              )} ${t('to')} ${format(new Date(original.endTime), 'h:mm')} ${t(
                format(new Date(original.endTime), 'aaa')
              )}`
            : '-';
        },
      }),
      columnHelper.accessor('workingHours', {
        header: t('total-hours'),
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('area', {
        header: t('total-area'),
        cell: ({ getValue }) => `${getValue()} ${t(areaUnit)}`,
      }),
      columnHelper.accessor('workingArea', {
        header: t('work-area'),
        cell: ({ getValue }) => `${getValue()} ${t(areaUnit)}`,
        footer: `${footerSum} ${t(areaUnit)}`,
      }),
    ];
  };

  const getProductsTable = () => {
    const columnHelper = createColumnHelper<IPeriodicTasks.IProductSchema>();
    return [
      columnHelper.accessor('productName', {
        header: t('items'),
        cell: ({ getValue, row: { original } }) => {
          const sortedItems: IWarehouseManagement.ActiveSubstanceSchema[] =
            original.activeSubstances?.sort(
              (a, b) => b.concentration - a.concentration
            ) ?? [];
          return (
            <div className='flex items-center gap-8'>
              <Typography weight='semiBold' className='text-primary-default'>
                {getValue()}
              </Typography>
              {original.activeSubstances && (
                <Tooltip
                  background={themeColors().background['neutral-99']}
                  className='react-tooltip-rounded'
                  arrow
                  arrowSize={10}
                  content={
                    <div className='flex w-fit min-w-[100px] flex-col px-16 py-8'>
                      {sortedItems.map((item) => (
                        <Typography variant='subtitle2' key={item.id}>
                          {item.name} {item.concentration}%
                        </Typography>
                      ))}
                    </div>
                  }
                  direction='bottom'
                  tagName='div'
                  tipContentHover
                  padding={0}
                >
                  <span className='cursor-pointer text-primary-default'>
                    <InfoCircle width='16' height='16' />
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor('rate', {
        header: t('rate-per-unit', { unit: t(areaUnit) }),
        cell: ({ getValue, row: { original } }) =>
          `${getValue()} ${t(original.unit)}/${t(areaUnit)}`,
      }),
      columnHelper.accessor('totalAmount', {
        header: t('total-amount'),
        cell: ({ getValue, row: { original } }) =>
          `${getValue()} ${t(original.unit)}`,
      }),
      columnHelper.display({
        id: 'expand',
        cell: ({ row }) =>
          row.getCanExpand() && (
            <div
              className='flex cursor-pointer items-center justify-end'
              onClick={row.getToggleExpandedHandler()}
            >
              <span
                className={classNames(
                  !row.getIsExpanded() && ' rotate-180',
                  'transition-all duration-300'
                )}
              >
                <Arrow />
              </span>
            </div>
          ),
      }),
    ];
  };

  const getSubrows = (products: IPeriodicTasks.IProductSchema[]) =>
    products.map((product) => ({
      ...product,
      subRows: product.details?.map((item) => ({
        productName: (
          <PartialListWithTooltip
            key={item.plotId}
            limit={1}
            items={[{ description: item.plotName, imageURL: item.cropImage }]}
          />
        ),
        rate: item.rate,
        unit: item.unit,
        totalAmount: item.amount,
      })),
    }));

  const getWaterQuantityCardItems = (
    data: IPeriodicTasks.IWaterQuantitySchema
  ): IIconWithTitleDescriptionProps[] => {
    return [
      {
        icon: <Speedometer width='32' height='32' />,
        iconGap: 'gap-16',
        title: t('rate-rate'),
        titleTypography,
        descriptionTypography,
        description: `${data.rate} ${t(data.unit)} ${t('per-unit', {
          unit: t(areaUnit),
        })}`,
        iconAlignment: 'center',
      },
      {
        icon: <PictureFrame width='32' height='32' />,
        iconGap: 'gap-16',
        title: t('total-amount'),
        titleTypography,
        descriptionTypography,
        description: `${formatNumericValue(
          data.totalAmount ? Number(data.totalAmount) : 0
        )} ${t(data.unit)}`,
        iconAlignment: 'center',
      },
    ];
  };

  return {
    getMainInfoCardItems,
    getHeaderCardProps,
    getFieldsTable,
    getProductsTable,
    getSubrows,
    getWaterQuantityCardItems,
  };
};
